import lod_ from "lodash";
import { useEffect, useState, memo, useCallback } from "react";
import { Select, MenuItem, FormControl, Icon, Checkbox, Switch, IconButton } from "@mui/material";
import MDBox from "components/Basics/MDBox";
import MDTypography from "components/Basics/MDTypography";
import { useLanguages } from "hooks/useLanguages";

/**
 * Row item for document in the list
 * @param {*} param0
 * @returns {JSX.Element} - RowItem
 */
const RowItem = ({ file, isSelected, onToggleSelection, onChange }) => {
	const { languages, loading: languagesLoading } = useLanguages();
	const [loading, setLoading] = useState(true);

	const [localState, setLocalState] = useState({});

	/**
	 * Handle click on URL to open in new tab
	 */
	const handleUrlClick = () => {
		if (!file?.url?.startWith("http")) {
			return;
		}
		window.open(file.url, "_blank", "noopener,noreferrer");
	};

	/**
	 * Handle selection toggle
	 */
	const handleSelectionToggle = useCallback(() => {
		onToggleSelection(file.url);
	}, [file.url, onToggleSelection]);

	/**
	 * Handle change for each input
	 * @param {string} key - Key to change
	 * @param {any} value - New value
	 */
	const onChangeHandle = (key, value) => {
		onChange(file.url, key, value);
	};

	/**
	 * Use effect to set the default values
	 */
	useEffect(() => {
		// If languages are still loading, do nothing
		if (languagesLoading) {
			return;
		}

		const defaultLocalState = {
			language: file.language || "",
			extractTextFromImage: file.metadatas?.extractTextFromImage || false
		};

		setLoading(true);
		const defaultStateLanguage = defaultLocalState.language;

		if (lod_.isNil(defaultStateLanguage) || lod_.isEmpty(defaultStateLanguage)) {
			// If no language is set, try to set the default language
			const defaultLanguage = languages.find(lang => lang.default === true);
			onChangeHandle("language", defaultLanguage?.code || "");
			setLocalState(prev => ({ ...prev, language: defaultLanguage?.code || "" }));
		} else {
			setLocalState(defaultLocalState);
		}

		setLoading(false);
	}, [languagesLoading, file]);

	if (loading) {
		return null;
	}

	return (
		<MDBox
			component="tr"
			bgColor={isSelected ? "info" : "white"}
			sx={{
				borderBottom: "1px solid #f0f0f0",
				cursor: "pointer"
			}}
			onClick={handleSelectionToggle}
		>
			{/* Checkbox */}
			<MDBox component="td" style={{ width: "5%", padding: "12px 16px", textAlign: "left" }}>
				<Checkbox checked={isSelected} />
			</MDBox>

			{/* File name */}
			<MDBox component="td" style={{ padding: "12px 16px", textAlign: "left" }}>
				<MDTypography
					variant="body2"
					fontWeight="medium"
					fontSize="small"
					color={isSelected ? "white" : "black"}
				>
					{file.name}
				</MDTypography>
			</MDBox>

			{/* Language selector */}
			<MDBox component="td" style={{ padding: "12px 16px", textAlign: "left" }}>
				{!languagesLoading && (
					<FormControl
						size="small"
						sx={{ minWidth: 80, width: "100%" }}
						onClick={e => e.stopPropagation()}
					>
						<Select
							value={localState.language}
							onChange={e => {
								const newLanguage = e.target.value;
								setLocalState(prev => ({ ...prev, language: newLanguage }));
								onChangeHandle("language", newLanguage);
							}}
							onOpen={e => e.stopPropagation()}
							onClose={e => e.stopPropagation()}
							displayEmpty
							sx={{
								fontSize: "0.875rem",
								"& .MuiSelect-select": {
									padding: "4px 8px"
								},
								"& fieldset": {
									backgroundColor: "white.main"
								}
							}}
							renderValue={selected => {
								if (!selected) {
									return <MDTypography color="text">-</MDTypography>;
								}
								const lang = languages.find(lang => lang.code === selected);
								return (
									<MDBox display="flex" alignItems="center" justifyContent="space-between">
										{lang?.logoURL && (
											<img
												src={lang.logoURL}
												alt={lang.name}
												style={{ width: 16, height: 12, marginRight: 8, zIndex: 4000 }}
											/>
										)}
										<MDTypography
											variant="override"
											color="black"
											sx={{
												zIndex: 4000
											}}
										>
											{lang ? lang.code : selected}
										</MDTypography>
										<Icon sx={{ pl: 3, zIndex: 4000 }} color="black">
											expand_more
										</Icon>
									</MDBox>
								);
							}}
						>
							{languages.map(language => (
								<MenuItem key={language.code} value={language.code} sx={{ fontSize: "0.875rem" }}>
									<MDBox display="flex" alignItems="center">
										{language.logoURL && (
											<img
												src={language.logoURL}
												alt={language.name}
												style={{ width: 16, height: 12, marginRight: 8 }}
											/>
										)}
										{language.name}
									</MDBox>
								</MenuItem>
							))}
						</Select>
					</FormControl>
				)}
			</MDBox>

			{/* Extract text from image */}
			<MDBox component="td" style={{ padding: "12px 16px", textAlign: "center" }}>
				<Switch
					checked={localState.extractTextFromImage}
					onClick={e => e.stopPropagation()}
					onChange={e => {
						const newValue = e.target.checked;
						setLocalState(prev => ({ ...prev, extractTextFromImage: newValue }));
						onChangeHandle("metadatas.extractTextFromImage", newValue);
					}}
					color="info"
				/>
			</MDBox>

			{/* URL */}
			<MDBox component="td" style={{ padding: "12px 16px", textAlign: "left" }}>
				<IconButton onClick={handleUrlClick} disabled={!file?.url?.startsWith("http")}>
					<Icon>visibility</Icon>
				</IconButton>
			</MDBox>
		</MDBox>
	);
};

export default memo(RowItem, (prevProps, nextProps) => {
	return (
		prevProps.isSelected === nextProps.isSelected && lod_.isEqual(prevProps.file, nextProps.file)
	);
});
